import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Check, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { DURATION, EASE_SIGNATURE } from "./motion";
import { IconBubble } from "./IconBubble";
import { Tile } from "./Tile";

type FlowState = "idle" | "running" | "success" | "error";

interface FlowButtonProps {
  name: string;
  icon: ReactNode;
  /** Trigger the flow; resolve on success, reject on failure. */
  onTrigger: () => Promise<void>;
  className?: string;
}

/** Favorite-flow trigger tile with running/success/error feedback. */
export function FlowButton({ name, icon, onTrigger, className }: FlowButtonProps) {
  const [state, setState] = useState<FlowState>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  const trigger = async () => {
    if (state === "running") return;
    if (resetTimer.current) clearTimeout(resetTimer.current);
    setState("running");
    try {
      await onTrigger();
      if (!mounted.current) return;
      setState("success");
    } catch {
      if (!mounted.current) return;
      setState("error");
    }
    resetTimer.current = setTimeout(() => {
      if (mounted.current) setState("idle");
    }, 2000);
  };

  const statusIcon: Record<FlowState, ReactNode> = {
    idle: icon,
    running: <Loader2 className="animate-spin" />,
    success: <Check />,
    error: <TriangleAlert />,
  };

  const resolvedDomain = state === "error" ? "security" : "flow";

  return (
    <Tile
      domain={resolvedDomain}
      active={state !== "idle"}
      interactive
      aria-busy={state === "running"}
      aria-label={`Run flow ${name}${state !== "idle" ? `, ${state}` : ""}`}
      className={cn("flex-row items-center gap-3", className)}
      onClick={() => void trigger()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          void trigger();
        }
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={state}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.7 }}
          transition={{ duration: DURATION.fast, ease: EASE_SIGNATURE }}
        >
          <IconBubble domain={resolvedDomain} filled={state === "success"}>
            {statusIcon[state]}
          </IconBubble>
        </motion.div>
      </AnimatePresence>
      <span className="truncate text-sm font-medium">{name}</span>
    </Tile>
  );
}
